/**
 * lib/financePdf.ts — branded A4 invoice & receipt PDFs (jsPDF).
 * Gold / cream / espresso identity. Returns a jsPDF doc; helpers give a
 * base64 string for email attachments and a download.
 */
import { jsPDF } from 'jspdf';
import type { FinanceDoc } from './types';
import type { Business } from '@/data/site';

const ESPRESSO: [number, number, number] = [74, 44, 26];
const ESPRESSO_DEEP: [number, number, number] = [46, 27, 16];
const GOLD: [number, number, number] = [201, 150, 46];
const MUTED: [number, number, number] = [122, 106, 88];
const LINE: [number, number, number] = [230, 217, 196];
const PAPER: [number, number, number] = [251, 246, 236];
const BLUSH: [number, number, number] = [197, 118, 129];

const pula = (n: number) =>
  `P ${Number(n || 0).toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function drawLotus(d: jsPDF, cx: number, baseY: number, s: number) {
  d.setFillColor(...GOLD);
  // centre petal + two side petals (simple fan)
  d.triangle(cx, baseY - 5 * s, cx - 1.4 * s, baseY, cx + 1.4 * s, baseY, 'F');
  d.triangle(cx - 2.6 * s, baseY - 3.2 * s, cx - 3.6 * s, baseY, cx - 0.4 * s, baseY, 'F');
  d.triangle(cx + 2.6 * s, baseY - 3.2 * s, cx + 3.6 * s, baseY, cx + 0.4 * s, baseY, 'F');
}

export function generateFinancePdf(docData: FinanceDoc, biz: Business): jsPDF {
  const d = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 18;
  const title =
    docData.kind === 'invoice' ? 'INVOICE' : docData.kind === 'quotation' ? 'QUOTATION' : 'RECEIPT';

  // ── Header band ──────────────────────────────────────
  d.setFillColor(...ESPRESSO_DEEP);
  d.rect(0, 0, W, 38, 'F');
  drawLotus(d, M + 4, 24, 1.7);
  d.setFont('times', 'normal');
  d.setTextColor(243, 234, 216);
  d.setFontSize(22);
  d.text(biz.name, M + 14, 22);
  d.setFont('helvetica', 'normal');
  d.setFontSize(8);
  d.setTextColor(...GOLD);
  d.text((biz.tagline || '').toUpperCase(), M + 14, 28, { charSpace: 1.2 });

  // doc title / number / date (right)
  d.setFont('times', 'bold');
  d.setFontSize(20);
  d.setTextColor(...GOLD);
  d.text(title, W - M, 20, { align: 'right' });
  d.setFont('helvetica', 'normal');
  d.setFontSize(9);
  d.setTextColor(243, 234, 216);
  d.text(docData.number, W - M, 27, { align: 'right' });
  d.text(new Date(docData.createdAt).toLocaleDateString('en-BW'), W - M, 32, { align: 'right' });

  // ── From / To ────────────────────────────────────────
  let y = 52;
  d.setFontSize(8);
  d.setTextColor(...GOLD);
  d.text('FROM', M, y);
  d.text('BILL TO', W / 2, y);
  d.setFontSize(10);
  d.setTextColor(...ESPRESSO);
  d.setFont('helvetica', 'bold');
  d.text(biz.legalName || biz.name, M, y + 6);
  d.text(docData.clientName || '—', W / 2, y + 6);
  d.setFont('helvetica', 'normal');
  d.setTextColor(...MUTED);
  d.setFontSize(9);
  d.text(d.splitTextToSize(biz.address, 80), M, y + 12);
  d.text(biz.phone, M, y + 22);
  if (docData.clientEmail) d.text(docData.clientEmail, W / 2, y + 12);

  // ── Items table ──────────────────────────────────────
  y = 86;
  d.setFillColor(...ESPRESSO);
  d.rect(M, y, W - 2 * M, 9, 'F');
  d.setTextColor(243, 234, 216);
  d.setFont('helvetica', 'bold');
  d.setFontSize(9);
  d.text('DESCRIPTION', M + 3, y + 6);
  d.text('QTY', W - M - 62, y + 6, { align: 'right' });
  d.text('UNIT', W - M - 34, y + 6, { align: 'right' });
  d.text('AMOUNT', W - M - 3, y + 6, { align: 'right' });
  y += 9;

  d.setFont('helvetica', 'normal');
  d.setTextColor(...ESPRESSO);
  docData.items.forEach((it, i) => {
    const amount = it.qty * it.unitPrice;
    if (i % 2 === 1) {
      d.setFillColor(...PAPER);
      d.rect(M, y, W - 2 * M, 9, 'F');
    }
    d.setFontSize(9.5);
    d.text(d.splitTextToSize(it.description || '—', 95), M + 3, y + 6);
    d.text(String(it.qty), W - M - 62, y + 6, { align: 'right' });
    d.text(pula(it.unitPrice), W - M - 34, y + 6, { align: 'right' });
    d.text(pula(amount), W - M - 3, y + 6, { align: 'right' });
    y += 9;
  });
  d.setDrawColor(...LINE);
  d.line(M, y, W - M, y);

  // ── Totals ───────────────────────────────────────────
  y += 8;
  const lx = W - M - 70;
  const rx = W - M - 3;
  const totalRow = (label: string, value: string, bold = false) => {
    d.setFont('helvetica', bold ? 'bold' : 'normal');
    d.setFontSize(bold ? 11 : 9.5);
    d.setTextColor(...(bold ? ESPRESSO : MUTED));
    d.text(label, lx, y);
    d.setTextColor(...ESPRESSO);
    d.text(value, rx, y, { align: 'right' });
    y += bold ? 8 : 6;
  };
  totalRow('Subtotal', pula(docData.subtotal));
  if (docData.vatRate > 0) totalRow(`VAT (${(docData.vatRate * 100).toFixed(0)}%)`, pula(docData.vat));
  d.setDrawColor(...GOLD);
  d.line(lx, y - 2, rx, y - 2);
  y += 2;
  const totalLabel =
    docData.kind === 'receipt' ? 'Paid' : docData.kind === 'quotation' ? 'Total' : 'Total due';
  totalRow(totalLabel, pula(docData.total), true);
  if (docData.giftCardApplied) {
    totalRow(`Gift card${docData.giftCardCode ? ` (${docData.giftCardCode})` : ''}`, `\u2013 ${pula(docData.giftCardApplied)}`);
    totalRow('Balance', pula(Math.max(0, docData.total - docData.giftCardApplied)), true);
  }

  // Quotations are estimates \u2014 note how long they hold.
  if (docData.kind === 'quotation') {
    y += 2;
    d.setFont('helvetica', 'italic');
    d.setFontSize(8.5);
    d.setTextColor(...MUTED);
    d.text('Valid for 7 days from the date above.', rx, y, { align: 'right' });
    y += 6;
  }

  // ── Notes ────────────────────────────────────────────
  if (docData.notes) {
    y += 6;
    d.setFont('helvetica', 'bold');
    d.setFontSize(8);
    d.setTextColor(...GOLD);
    d.text('NOTES', M, y);
    d.setFont('helvetica', 'normal');
    d.setFontSize(9);
    d.setTextColor(...MUTED);
    d.text(d.splitTextToSize(docData.notes, W - 2 * M), M, y + 5);
  }

  // ── Void watermark ───────────────────────────────────
  if (docData.status === 'void') {
    d.setFont('times', 'bold');
    d.setFontSize(90);
    d.setTextColor(...BLUSH);
    d.saveGraphicsState();
    // @ts-expect-error — GState opacity is supported at runtime
    d.setGState(new d.GState({ opacity: 0.18 }));
    d.text('VOID', W / 2, 170, { align: 'center', angle: 30 });
    d.restoreGraphicsState();
  }

  // ── Footer ───────────────────────────────────────────
  d.setDrawColor(...LINE);
  d.line(M, 276, W - M, 276);
  d.setFont('times', 'italic');
  d.setFontSize(11);
  d.setTextColor(...ESPRESSO);
  d.text('Thank you. Relax. Refresh. Rejuvenate.', W / 2, 284, { align: 'center' });
  d.setFont('helvetica', 'normal');
  d.setFontSize(8);
  d.setTextColor(...MUTED);
  d.text(`${biz.legalName || biz.name}  ·  ${biz.address}  ·  ${biz.phone}`, W / 2, 290, { align: 'center' });

  return d;
}

export const financePdfFilename = (docData: FinanceDoc) => `${docData.number}.pdf`;

export function financePdfBase64(docData: FinanceDoc, biz: Business): string {
  const out = generateFinancePdf(docData, biz).output('datauristring');
  return out.split(',')[1] ?? '';
}

export function downloadFinancePdf(docData: FinanceDoc, biz: Business): void {
  generateFinancePdf(docData, biz).save(financePdfFilename(docData));
}
